import { InlineValue, ContentLink, useContentSingletons, usePageContent, useMultipleContents } from "@robe/client/xcore-ui";
import { fetchRelationsForSingleContent } from "@robe/core";
import { Box, Container, Grid, Typography } from "@xcorejs/ui";
import ImagesCarousel from "components/Carousel/ImagesCarousel";
import Layout from "components/Layout";
import RobeHead from "components/RobeHead";
import VideoCarousel from "components/Carousel/VideoCarousel";
import ProductCard, {
  ProductCardAccessoryValues,
  productCardProductFields,
  ProductCardProductOrAccessory,
  ProductCardProductValues,
} from "design-system/robe/Cards/Product";
import { useMemo } from "react";
import { RobePage, getRobePaths, getRobeProps, useLayout } from "xcore";
import { Innovation, Innovations } from "xcore/types";
import { GetProductGridResult } from "pages/api/robe/product-grid";
import Richtext from "design-system/Richtext";
import { PageIframe } from "design-system/robe/PageIframe";
import { VideoComponentType } from "components/Video";
import { useFrontendQuery } from "hooks/useFrontendQuery";

type ProductOrAccessoryValues = ProductCardAccessoryValues & ProductCardProductValues;

export const getStaticProps = getRobeProps<Innovation>(async ({ core, id, locale }) => {
  const reverseRelations = await core.content.getReverseRelations(id!);
  const productsReverseRelationsIDs = reverseRelations
    .filter(({ contentType }) => contentType === "product" || contentType === "accessory")
    .map(({ contentId }) => contentId);

  return {
    pageContent: core.content
      .singleById(id!, {
        includeValues: true,
      })
      .then(
        fetchRelationsForSingleContent({
          core,
          relationSourceFields: [{ field: "video" }],
          options: {
            includeValues: true,
          },
        })
      ),
    contentSingletons: [
      core.content.singletonByType<Innovations>("innovations", {
        fields: ["featuredOn"],
      }),
    ],
    ...(productsReverseRelationsIDs.length > 0 && {
      multipleContents: [
        core.content.listByIds<ProductCardProductOrAccessory, ProductOrAccessoryValues>(productsReverseRelationsIDs, {
          includeCategories: true,
          fields: productCardProductFields,
          // todo xcc after localized images migrated remove workaround
          ...(locale === "ru" && {
            locales: ["ru", "en"],
          }),
        }),
      ],
    }),
  };
});

export const getStaticPaths = getRobePaths("innovation");

const InnovationDetail: RobePage = () => {
  const {
    core: { localeContent },
    localize,
    stringify,
    image,
  } = useLayout();
  const { id, values } = usePageContent<Innovation>();
  const [innovations] = useContentSingletons<[Innovations]>();
  const [relatedProductsAndAccessories] = useMultipleContents<[ProductCardProductOrAccessory]>() as [
    ProductCardProductOrAccessory[] | undefined,
  ];

  const { data: sortBy } = useFrontendQuery<GetProductGridResult>(
    `/api/robe/product-grid?${localeContent.map((l) => `locale=${l}`).join("&")}`
  );
  const typeList = sortBy?.productTypes.map((pt) => pt.id);
  const sizeList = sortBy?.productSizes.map((ps) => ps.id);

  const relatedProductsAndAccessoriesSorted = useMemo(() => {
    if (!relatedProductsAndAccessories) {
      return [];
    }

    const getPriority = (item: ProductCardProductOrAccessory) => {
      // Discontinued products have the lowest priority (e.g., 2)
      if (item.values.discontinuedGlob || localize(item.values.discontinuedReg)) {
        return 2;
      }

      // Products without valid type or size have medium priority (e.g., 1)
      const productType = typeList ? item.subCategories?.["product-type"]?.find((pt) => typeList.includes(pt.id)) : null;
      const productSize = item.type === "product" ? item.subCategories?.["product-size"]?.[0] : null;

      if (!productType || !productSize || !typeList || !sizeList) {
        return 1;
      }

      // Products with valid type and size have the highest priority (e.g., 0)
      return 0;
    };

    const getTypeOrder = (item: ProductCardProductOrAccessory) => {
      const productType = typeList ? item.subCategories?.["product-type"]?.find((pt) => typeList.includes(pt.id)) : null;
      return productType && typeList ? typeList.indexOf(productType.id) : Infinity;
    };

    const getSizeOrder = (item: ProductCardProductOrAccessory) => {
      const productSize = item.type === "product" ? item.subCategories?.["product-size"]?.[0] : null;
      return productSize && sizeList ? sizeList.indexOf(productSize.id) : Infinity;
    };

    // Map products and sort by priority, type order, and size order
    return relatedProductsAndAccessories
      .map((product) => ({
        ...product,
        priority: getPriority(product),
        typeOrder: getTypeOrder(product),
        sizeOrder: getSizeOrder(product),
      }))
      .sort((a, b) => a.priority - b.priority || a.typeOrder - b.typeOrder || a.sizeOrder - b.sizeOrder);
  }, [relatedProductsAndAccessories, localize, sizeList, typeList]);

  if (stringify(values.url)) {
    return (
      <Container width="100%" px="0">
        <PageIframe src={stringify(values.url)} />
      </Container>
    );
  }

  const images = localize(values?.images);
  const innovationImageUrl = values?.image ? image(values?.image, { width: 410, height: 230 }) : null;
  const videosFiltered = values.video?.filter((v) => v.values && v.type && v.title) as VideoComponentType[];

  return (
    <>
      <RobeHead
        title={[stringify(innovations.title), stringify(values.name)]}
        description={stringify(values.intro)}
        {...(innovationImageUrl && { img: innovationImageUrl })}
      />

      <Container flexDirection="column" paddingTop={{ _: "3rem", sm: "5rem" }} paddingBottom={{ _: "6rem", sm: "10rem" }}>
        <Typography variant="h1" mb={{ _: "2rem", sm: "3rem" }} textAlign="center">
          <InlineValue value={stringify(values.name)} />
        </Typography>

        <div dangerouslySetInnerHTML={{ __html: stringify(values.html) }}></div>

        <Box mb="6rem">
          <Richtext
            value={values.description}
            _paragraph={{
              variant: "lead",
              textAlign: "center",
              minHeight: { _: "2.7rem", sm: "3.6rem" },
            }}
            _link={{
              fontSize: "inherit",
              lineHeight: "inherit",
            }}
          />
        </Box>

        {videosFiltered && (
          <Box width={videosFiltered.length === 1 ? "74rem" : "100%"} maxWidth="100%" margin="auto" mb={{ _: "4rem", sm: "8rem" }}>
            <VideoCarousel videos={videosFiltered} loop={values.loop} autoplay={values.autoplay} openInModal={values.openInModal} />
          </Box>
        )}

        {images && (
          <Box mb={{ _: "2rem", sm: "8rem" }}>
            <Box>
              <ImagesCarousel
                disableScroll
                hideSubHeader
                images={images.map((img) => ({
                  src: image(img, images?.length > 1 ? { width: 740, height: 416 } : { width: 1290, height: 726 }),
                  alt: img.id,
                  retina: image(
                    img,
                    images.length > 1 ? { width: 740, height: 416, enlargement: 2 } : { width: 1290, height: 726, enlargement: 2 }
                  ),
                }))}
              />
            </Box>
          </Box>
        )}

        {relatedProductsAndAccessoriesSorted.length > 0 && (
          <Box>
            <Typography variant="h3" as="h2" textAlign="center" mb={{ _: "2rem", sm: "3rem" }}>
              <InlineValue value={innovations.values.featuredOn} />
            </Typography>

            <Grid columns={{ _: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} rows="auto" gap={{ _: "2rem", sm: "3rem" }}>
              {relatedProductsAndAccessoriesSorted.map((item) => (
                <ContentLink noUILink key={item.id} queries={{ backto: id }} content={item} variant="simple" overflow="hidden">
                  <ProductCard product={item} />
                </ContentLink>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </>
  );
};

InnovationDetail.Layout = Layout;

export default InnovationDetail;
